import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MENU_CONFIG } from "src/constants/menuConfig";

interface SearchEntry {
  label: string;
  path: string;
  groupLabel: string;
}

const buildSearchIndex = (): SearchEntry[] => {
  const entries: SearchEntry[] = [];
  Object.values(MENU_CONFIG).forEach((menu: any) => {
    if (menu.path) {
      entries.push({ label: menu.label, path: menu.path, groupLabel: menu.label });
    }
    (menu.subMenu || []).forEach((sub: any) => {
      if (!sub.path) return;
      entries.push({ label: sub.label, path: sub.path, groupLabel: menu.label });
    });
  });
  return entries;
};

const SEARCH_INDEX = buildSearchIndex();

const TopbarSearch: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SEARCH_INDEX;
    return SEARCH_INDEX.filter((entry) => entry.label.toLowerCase().includes(q));
  }, [query]);

  const closeModal = () => {
    setOpen(false);
    setQuery("");
    setActiveIdx(0);
  };

  useEffect(() => {
    if (open) {
      // Let the modal mount before focusing.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  const goTo = (entry: SearchEntry) => {
    navigate(entry.path);
    closeModal();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      closeModal();
      return;
    }
    if (results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      goTo(results[activeIdx]);
    }
  };

  return (
    <>
      <button
        type="button"
        className="w-full flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
        onClick={() => setOpen(true)}
      >
        <Search size={16} className="text-gray-400 shrink-0" />
        <span className="text-sm text-gray-400">Search or jump to...</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-start justify-center pt-24 overflow-y-auto"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b dark:border-gray-700">
              <Search size={18} className="text-gray-400 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                placeholder="Search or jump to..."
                className="w-full bg-transparent text-base text-gray-800 placeholder:text-gray-400 focus:outline-none dark:text-gray-100"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <ul className="max-h-96 overflow-y-auto py-1">
              {results.length === 0 ? (
                <li className="px-4 py-8 text-center text-sm text-gray-500">
                  No matches for "{query}"
                </li>
              ) : (
                results.map((entry, idx) => (
                  <li key={`${entry.path}-${idx}`}>
                    <button
                      className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between ${
                        idx === activeIdx
                          ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                          : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/60"
                      }`}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => goTo(entry)}
                    >
                      <span>{entry.label}</span>
                      <span className="text-xs text-gray-400">
                        {entry.groupLabel}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default TopbarSearch;
